import { useState } from "react";
import { Link } from "react-router-dom";
import { computeStandings, groupProgress, type Game } from "@/lib/standings";
import { isFinal } from "@/lib/time";
import { Trophy, Medal, ChevronRight } from "lucide-react";
import { teamDe } from "@/lib/teamNames";
import { PLACEHOLDER_BRACKET } from "@/lib/placeholderBracket";

// Rounds shown as bracket columns / mobile chips. Final + 3rd place get their
// own hero section below, so they're intentionally NOT in this list.
const PHASE_ORDER: { key: string; label: string; short: string }[] = [
  { key: "r32", label: "Sechzehntelfinale", short: "Sechzehntel" },
  { key: "r16", label: "Achtelfinale", short: "Achtel" },
  { key: "qf", label: "Viertelfinale", short: "Viertel" },
  { key: "sf", label: "Halbfinale", short: "Halb" },
];

type Status = "upcoming" | "live" | "finished";

const isSynthetic = (g: Game) => String(g.id).startsWith("placeholder-");
const isSlotLabel = (t: string) => /^(Sieger|Zweiter|Drittplatzierter|Verlierer)/i.test(t);

function statusOf(g: Game): Status {
  if (g.is_finished && g.home_score != null && g.away_score != null) return "finished";
  const ks = g.kickoff_time ? new Date(g.kickoff_time).getTime() : 0;
  const started = !!ks && Date.now() >= ks && !isFinal(g.kickoff_time);
  if (g.is_live || started) return "live";
  return "upcoming";
}

// Compact "Sa, 28.06. · 21:00" for upcoming cards.
function shortKickoff(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "Europe/Berlin" });
  const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
  return `${day} · ${time}`;
}

function TeamRow({
  team, flag, score, won, lost, live, placeholder,
}: {
  team: string; flag: string; score: number | null; won: boolean; lost: boolean; live: boolean; placeholder: boolean;
}) {
  return (
    <div className={`flex items-center gap-2 px-2.5 py-2 ${won ? "bg-success/8" : ""}`}>
      <span className="text-lg leading-none w-6 text-center shrink-0">{placeholder ? "❓" : flag}</span>
      <span
        className={`flex-1 truncate text-[13px] leading-tight ${
          placeholder ? "italic text-muted-foreground font-normal"
          : won ? "font-extrabold"
          : lost ? "font-semibold text-muted-foreground"
          : "font-semibold"
        }`}
      >
        {teamDe(team)}
      </span>
      <span
        className={`tabular-nums font-extrabold text-[15px] w-5 text-right shrink-0 ${
          live ? "text-primary" : won ? "text-foreground" : "text-muted-foreground"
        }`}
      >
        {score ?? "–"}
      </span>
    </div>
  );
}

function MatchCard({ game, accent }: { game: Game; accent?: "gold" | "bronze" }) {
  const synthetic = isSynthetic(game);
  const st = statusOf(game);
  const live = st === "live";

  const hs = st === "finished" ? game.home_score : live ? (game.live_home_score ?? 0) : null;
  const as = st === "finished" ? game.away_score : live ? (game.live_away_score ?? 0) : null;
  const homeWon = st === "finished" && game.home_score! > game.away_score!;
  const awayWon = st === "finished" && game.away_score! > game.home_score!;

  const ss = game.status_short ?? "";
  const finLabel = ss === "PEN" ? "i.E." : ss === "AET" ? "n.V." : "Beendet";
  const statusText = live ? (ss && ss !== "LIVE" ? ss : "LIVE")
    : st === "finished" ? finLabel
    : synthetic ? "Vorschau" : shortKickoff(game.kickoff_time);

  const headerCls =
    accent === "gold" ? "bg-yellow-400/15 text-yellow-700"
    : accent === "bronze" ? "bg-amber-500/15 text-amber-700"
    : live ? "bg-primary/10 text-primary"
    : "bg-muted/60 text-muted-foreground";

  const borderCls =
    accent === "gold" ? "border-yellow-400/70"
    : accent === "bronze" ? "border-amber-500/50"
    : live ? "border-primary/60"
    : "border-border";

  const inner = (
    <>
      <div className={`flex items-center justify-between px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${headerCls}`}>
        <span className="flex items-center gap-1">
          {accent === "gold" && <Trophy className="h-3 w-3" />}
          {accent === "bronze" && <Medal className="h-3 w-3" />}
          #{game.game_no}
        </span>
        <span className="flex items-center gap-1">
          {live && <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />}
          {statusText}
          {!synthetic && <ChevronRight className="h-3 w-3 opacity-50" />}
        </span>
      </div>
      <TeamRow team={game.home_team} flag={game.home_flag} score={hs} won={homeWon} lost={awayWon} live={live} placeholder={isSlotLabel(game.home_team)} />
      <div className="h-px bg-border" />
      <TeamRow team={game.away_team} flag={game.away_flag} score={as} won={awayWon} lost={homeWon} live={live} placeholder={isSlotLabel(game.away_team)} />
    </>
  );

  const base = `block w-full overflow-hidden rounded-xl border-2 ${borderCls} bg-card`;
  if (synthetic) {
    return <div className={`${base} border-dashed opacity-90`}>{inner}</div>;
  }
  return (
    <Link to={`/spiel/${game.id}`} className={`${base} shadow-card tap transition-all hover:shadow-pop hover:-translate-y-0.5`}>
      {inner}
    </Link>
  );
}

export function KOBracket({ games }: { games: Game[] }) {
  const byPhase: Record<string, Game[]> = {};
  for (const g of games) {
    if (g.phase !== "group") (byPhase[g.phase] = byPhase[g.phase] ?? []).push(g);
  }

  // Which KO game numbers already have a REAL synced game? Captured BEFORE
  // injecting placeholders, and scoped PER PHASE. We fill per game-number so a
  // partially-drawn round — e.g. only one semifinal (#101) synced while #102
  // still carries TBD teams from the feed — still previews its remaining
  // matches instead of the whole round's placeholders vanishing.
  //
  // Scoping is per-phase (not a single global set) because real games get a
  // SEQUENTIAL game_no from the sync (nextGameNo++), which is NOT guaranteed to
  // equal the placeholder bracket's FIFA fixture numbers. A real game that
  // lands on sequential #102 in ANOTHER phase (e.g. the final or 3rd-place
  // game) must NOT suppress the Halbfinale placeholder #102 — only a real game
  // in the SAME phase with that number should.
  const realGameNosByPhase: Record<string, Set<number>> = {};
  for (const [phase, list] of Object.entries(byPhase))
    for (const g of list) (realGameNosByPhase[phase] ??= new Set()).add(g.game_no);

  // Per-phase preview: inject the placeholder bracket only for rounds with NO
  // real games yet, so synced Round-of-32 fixtures show as real, tippable
  // games while later (undrawn) rounds still preview. Resolve "Sieger X" /
  // "Zweiter X" to the actual qualified team once that group is fully played.
  // Third-place ("Drittplatzierter …") + "Sieger #NN" stay labels: best-thirds
  // only firm up once all groups are done, and #NN winners need real results.
  const standings = computeStandings(games);
  const decided = new Set<string>();
  for (const label of Object.keys(standings)) {
    if (!/^[A-L]$/.test(label)) continue;
    const prog = groupProgress(games, label);
    if (prog.total > 0 && prog.played === prog.total) decided.add(label);
  }
  const resolveSlot = (label: string): { team: string; flag: string } | null => {
    const w = label.match(/^Sieger ([A-L])$/);
    const z = label.match(/^Zweiter ([A-L])$/);
    if (w && decided.has(w[1])) {
      const row = standings[w[1]]?.[0];
      if (row) return { team: row.team, flag: row.flag };
    }
    if (z && decided.has(z[1])) {
      const row = standings[z[1]]?.[1];
      if (row) return { team: row.team, flag: row.flag };
    }
    return null;
  };
  let hasPlaceholders = false;
  for (const g of PLACEHOLDER_BRACKET) {
    if (realGameNosByPhase[g.phase]?.has(g.game_no)) continue;
    hasPlaceholders = true;
    const h = resolveSlot(g.home_team);
    const a = resolveSlot(g.away_team);
    byPhase[g.phase] = byPhase[g.phase] ?? [];
    byPhase[g.phase].push({
      ...g,
      home_team: h?.team ?? g.home_team,
      home_flag: h?.flag ?? g.home_flag,
      away_team: a?.team ?? g.away_team,
      away_flag: a?.flag ?? g.away_flag,
    } as unknown as Game);
  }
  for (const k of Object.keys(byPhase)) byPhase[k].sort((a, b) => a.game_no - b.game_no);

  const third = byPhase["third"]?.[0];
  const finalGame = byPhase["final"]?.[0];

  // Mobile round selector. Default to the most relevant round: the one with a
  // live game, else the earliest round with an unfinished REAL game, else the
  // first round that has anything.
  const present = PHASE_ORDER.filter((p) => (byPhase[p.key]?.length ?? 0) > 0);
  const liveRound = present.find((p) => byPhase[p.key].some((g) => statusOf(g) === "live"));
  const nextRound = present.find((p) => byPhase[p.key].some((g) => !isSynthetic(g) && statusOf(g) !== "finished"));
  const defaultRound = (liveRound ?? nextRound ?? present[0])?.key ?? "r32";
  const [selected, setSelected] = useState<string | null>(null);
  const active = selected ?? defaultRound;
  const activeGames = byPhase[active] ?? [];

  return (
    <div className="space-y-5">
      {hasPlaceholders && (
        <div className="rounded-lg bg-accent/10 border border-accent/30 px-3 py-2.5 text-[12px] flex items-start gap-2">
          <span className="text-base leading-none">🏆</span>
          <div>
            <b>KO-Phase Vorschau</b> — noch nicht ausgeloste Runden werden als
            Vorschau angezeigt. Die Plätze füllen sich automatisch mit den echten
            Mannschaften (und werden tippbar), sobald die Paarungen feststehen.
          </div>
        </div>
      )}

      {/* ───────── Mobile / tablet: round selector + focused card grid ───────── */}
      <div className="lg:hidden space-y-3">
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1 no-scrollbar">
          {present.map((p) => {
            const list = byPhase[p.key];
            const hasLive = list.some((g) => statusOf(g) === "live");
            const on = active === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setSelected(p.key)}
                className={`shrink-0 flex items-center gap-1.5 rounded-pill px-3.5 py-2 text-[13px] font-bold tap transition ${
                  on ? "bg-primary text-primary-foreground shadow-card" : "bg-card border border-border text-muted-foreground"
                }`}
              >
                {hasLive && <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-primary-foreground" : "bg-primary"} animate-pulse`} />}
                {p.short}
                <span className={`tabular-nums text-[11px] ${on ? "opacity-80" : "opacity-60"}`}>{list.length}</span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {activeGames.map((g) => (
            <MatchCard key={g.id} game={g} />
          ))}
        </div>
      </div>

      {/* ───────── Desktop: full connected bracket, all rounds side by side ───────── */}
      <div className="hidden lg:block overflow-x-auto -mx-4 px-4 pb-2">
        <div className="flex items-stretch gap-5 min-w-max" style={{ minHeight: "min(78vh, 880px)" }}>
          {present.map((phase) => {
            const list = byPhase[phase.key];
            const hasLive = list.some((g) => statusOf(g) === "live");
            return (
              <div key={phase.key} className="flex flex-col w-[212px]">
                <div className="flex items-center justify-center gap-1.5 mb-2.5">
                  <span className="text-[11px] font-display font-extrabold uppercase tracking-wide text-foreground">{phase.label}</span>
                  {hasLive && <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />}
                </div>
                <div className="flex-1 grid gap-2.5" style={{ gridTemplateRows: `repeat(${list.length}, minmax(0,1fr))` }}>
                  {list.map((g) => (
                    <div key={g.id} className="flex items-center">
                      <MatchCard game={g} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ───────── Final + 3rd place hero (always shown) ───────── */}
      {(finalGame || third) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {finalGame && (
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/70 border-2 border-yellow-400 rounded-2xl p-4 shadow-pop">
              <div className="flex items-center gap-2 mb-2.5">
                <Trophy className="h-5 w-5 text-yellow-600" />
                <span className="font-display font-extrabold text-lg">Finale</span>
              </div>
              <MatchCard game={finalGame} accent="gold" />
            </div>
          )}
          {third && (
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/70 border-2 border-amber-500/60 rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-2 mb-2.5">
                <Medal className="h-5 w-5 text-amber-700" />
                <span className="font-display font-bold text-base">Spiel um Platz 3</span>
              </div>
              <MatchCard game={third} accent="bronze" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
